import * as React from "react"
import { useState, useEffect, useRef } from "react"
import { ArrowUp } from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"

interface AIChatInputProps {
  placeholders?: string[]
  value?: string
  onChange?: (value: string) => void
  onSubmit?: (value: string) => void
  disabled?: boolean
}

const DEFAULT_PLACEHOLDERS = [
  "Ask anything...",
  "What would you like to know?",
  "How can I help you today?",
  "Describe what you need...",
]

const AIChatInput = ({
  placeholders = DEFAULT_PLACEHOLDERS,
  value: controlledValue,
  onChange,
  onSubmit,
  disabled = false,
}: AIChatInputProps) => {
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [showPlaceholder, setShowPlaceholder] = useState(true)
  const [internalValue, setInternalValue] = useState("")
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const inputValue = controlledValue !== undefined ? controlledValue : internalValue
  const setInputValue = (v: string) => {
    if (onChange) onChange(v)
    else setInternalValue(v)
  }

  const isActive = !!(inputValue || document.activeElement === inputRef.current)

  useEffect(() => {
    if (isActive || inputValue) return

    const interval = setInterval(() => {
      setShowPlaceholder(false)
      setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholders.length)
        setShowPlaceholder(true)
      }, 400)
    }, 3000)

    return () => clearInterval(interval)
  }, [isActive, inputValue, placeholders.length])

  const handleSubmit = () => {
    if (!inputValue.trim() || disabled) return
    onSubmit?.(inputValue.trim())
    setInternalValue("")
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const placeholderContainerVariants = {
    initial: {},
    animate: { transition: { staggerChildren: 0.02 } },
    exit: { transition: { staggerChildren: 0.012, staggerDirection: -1 } },
  }

  const letterVariants = {
    initial: { opacity: 0, filter: "blur(8px)", y: 8 },
    animate: {
      opacity: 1,
      filter: "blur(0px)",
      y: 0,
      transition: {
        opacity: { duration: 0.2 },
        filter: { duration: 0.35 },
        y: { type: "spring", stiffness: 100, damping: 20 },
      },
    },
    exit: {
      opacity: 0,
      filter: "blur(8px)",
      y: -8,
      transition: {
        opacity: { duration: 0.15 },
        filter: { duration: 0.25 },
        y: { type: "spring", stiffness: 100, damping: 20 },
      },
    },
  }

  return (
    <div
      ref={wrapperRef}
      className="w-full max-w-3xl mx-auto rounded-2xl border border-[#eee8dc]/[0.06] bg-[#eee8dc]/[0.03]"
    >
      <div className="flex items-center gap-2 px-4 py-2.5">
        <div className="relative flex-1">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            rows={1}
            className="w-full resize-none border-0 outline-0 py-2 text-[15px] bg-transparent font-body text-[#eee8dc] placeholder-transparent"
            style={{ position: "relative", zIndex: 1 }}
          />
          <div className="absolute left-0 top-0 w-full h-full pointer-events-none flex items-center">
            <AnimatePresence mode="wait">
              {showPlaceholder && !isActive && !inputValue && (
                <motion.span
                  key={placeholderIndex}
                  className="absolute left-0 top-1/2 -translate-y-1/2 text-[#eee8dc]/20 select-none pointer-events-none font-body text-[15px]"
                  style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", zIndex: 0 }}
                  variants={placeholderContainerVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {placeholders[placeholderIndex].split("").map((char, i) => (
                    <motion.span key={i} variants={letterVariants} style={{ display: "inline-block" }}>
                      {char === " " ? " " : char}
                    </motion.span>
                  ))}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); handleSubmit() }}
          disabled={!inputValue.trim() || disabled}
          className="p-2.5 rounded-xl bg-[#6e1f1b] text-[#eee8dc] transition-all duration-200 hover:bg-[#8a2822] disabled:opacity-20 disabled:cursor-not-allowed"
          title="Send"
          type="button"
        >
          <ArrowUp size={18} />
        </button>
      </div>
    </div>
  )
}

export { AIChatInput }
